import { z } from "zod";

export const ColumnVisibilityEntrySchema = z.object({
  columnId: z.string().min(1),
  visible: z.boolean(),
});

export const ColumnSizingEntrySchema = z.object({
  columnId: z.string().min(1),
  width: z.number(),
});

/**
 * What one view of a list shows (#6714), as the server answers it: the tab and the URL's view
 * parameters, and the columns. Column choices are lists so that a column id comes back exactly as it
 * was written.
 */
export const TableViewStateSchema = z.object({
  tabPath: z.string().nullish(),
  search: z.string().nullish(),
  columnVisibility: z.array(ColumnVisibilityEntrySchema).nullish(),
  columnSizing: z.array(ColumnSizingEntrySchema).nullish(),
  columnOrder: z.array(z.string()).nullish(),
});

/** The view the reader left a table in, and the saved view it started from (null: the Default view). */
export const CurrentTableViewSchema = z.object({
  viewId: z.string().nullish(),
  state: TableViewStateSchema,
  updatedAt: z.string(),
});

/** A view the reader saved under a name, for the Views menu (#6717). */
export const NamedTableViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number(),
  isStarred: z.boolean(),
  state: TableViewStateSchema,
  updatedAt: z.string(),
});

export const TableColumnPreferenceSchema = z.object({
  tableKey: z.string().min(1),
  /** The current view's Show/Hide columns choice: what this answer held before views. */
  columns: z.array(ColumnVisibilityEntrySchema),
  currentView: CurrentTableViewSchema.nullish(),
  views: z.array(NamedTableViewSchema).optional(),
  updatedAt: z.string(),
});

export const TableColumnPreferencesSchema = z.object({
  tables: z.array(TableColumnPreferenceSchema),
});

export type ColumnVisibilityEntry = z.infer<typeof ColumnVisibilityEntrySchema>;
export type ColumnSizingEntry = z.infer<typeof ColumnSizingEntrySchema>;
export type TableViewStateJSON = z.infer<typeof TableViewStateSchema>;
export type CurrentTableViewJSON = z.infer<typeof CurrentTableViewSchema>;
export type NamedTableViewJSON = z.infer<typeof NamedTableViewSchema>;
export type TableColumnPreferenceJSON = z.infer<typeof TableColumnPreferenceSchema>;
export type TableColumnPreferencesJSON = z.infer<typeof TableColumnPreferencesSchema>;
